/**
 * A aba "Ajuda de Custo" da Calculadora Petrella.
 *
 * Papel: reproduz `#ac-tab-content-custo` (l. 966–1058) e
 * `atualizarCalculadoraAjudaCusto` (l. 5060–5107): funcionário e CPF; pagador,
 * mês e ano; a data com "Repetir último lançamento"; o 1/3 de férias; os
 * quatro cartões de tipo com os subtotais; o quadro de férias; e o total geral.
 *
 * Como usar: os campos são de quem monta (`CamposDaAjudaDeCusto`), que os
 * recebe de volta por `onMudar` a cada tecla e a cada marca; os subtotais, o
 * quadro e o total são refeitos daqui sobre `entradaDaCalculadora`. Digitar o
 * funcionário passa por `aoEscolherFuncionario`, que filtra os cartões e
 * preenche CPF e valores vazios pelo perfil. "Repetir último lançamento" só
 * chama `onRepetir`: a leitura do grupo é de quem monta.
 *
 * O subtotal à vista é `diária × dias × 4/3` sem arredondar, e o item gravado é
 * arredondado sozinho: o centavo de diferença entre os dois é da origem.
 */
import {
  type Favorecido,
  MESES_POR_EXTENSO,
  type NomeDaFolha,
  subtotaisDaCalculadora,
} from '@concierge/shared';
import { IconRepeat } from '@tabler/icons-react';

import { mascararCentavos, mascararDecimalLivre } from '@/shared/lib/mascara';
import { cn } from '@/shared/lib/utils';
import {
  type CamposDaAjudaDeCusto,
  type CartaoComDiaria,
  type CartaoFixo,
  aoEscolherFuncionario,
  comMesEAno,
  entradaDaCalculadora,
  funcionariosDaCalculadora,
} from '../calculadora';
import { formatarReais } from '../formato';
import { Botao } from './kit/botao';
import { CLASSE_DO_CONTROLE, CLASSE_DO_SELECT, Campo, LinhaDeCampos } from './kit/campo';
import { CartaoDeTipo } from './kit/cartao-de-tipo';

/** O subtotal de um cartão: 700, `padding:8px 0`. */
const CLASSE_DO_SUBTOTAL = 'py-2 font-bold text-foreground';

/** A caixa de marcar com o nome do tipo, no alto de cada cartão. */
const CLASSE_DA_MARCA = 'flex cursor-pointer items-center gap-2 select-none';

/** A nota dos dois tipos que não recebem o 1/3 de férias. */
const SEM_FERIAS = 'Não recebe o 1/3 de férias, mesmo com a opção acima marcada.';

interface AbaAjudaDeCustoProps {
  campos: CamposDaAjudaDeCusto;
  onMudar: (campos: CamposDaAjudaDeCusto) => void;
  /** Os favorecidos da casa, para a lista e o preenchimento pelo perfil. */
  favorecidos: readonly Favorecido[];
  /** O mapa CPF→nome, para o CPF que o perfil não tem. */
  nomes: readonly NomeDaFolha[];
  /** O "Repetir último lançamento". */
  onRepetir: () => void;
}

/**
 * A aba "Ajuda de Custo".
 *
 * @param campos - Os campos como estão na tela.
 * @param onMudar - Recebe os campos depois de cada mudança.
 * @param favorecidos - Os favorecidos da casa.
 * @param nomes - O mapa CPF→nome da casa.
 * @param onRepetir - Chamado por "Repetir último lançamento".
 * @returns Os campos, os cartões e o total.
 */
export function AbaAjudaDeCusto({
  campos,
  onMudar,
  favorecidos,
  nomes,
  onRepetir,
}: AbaAjudaDeCustoProps) {
  const subtotais = subtotaisDaCalculadora(entradaDaCalculadora(campos));
  const visivel = (tipo: 'custo' | 'diarias' | 'medica' | 'inss') =>
    campos.visiveis === null || campos.visiveis.includes(tipo);
  const algumIncluido =
    campos.custo.incluido ||
    campos.diarias.incluido ||
    campos.medica.incluido ||
    campos.inss.incluido;

  /**
   * Troca um cartão com diária.
   *
   * @param tipo - `custo` ou `diarias`.
   * @param mudanca - A marca, a diária ou os dias.
   */
  function handleCartaoComDiaria(
    tipo: 'custo' | 'diarias',
    mudanca: Partial<CartaoComDiaria>,
  ) {
    onMudar({ ...campos, [tipo]: { ...campos[tipo], ...mudanca } });
  }

  /**
   * Troca um cartão de valor fixo.
   *
   * @param tipo - `medica` ou `inss`.
   * @param mudanca - A marca ou o valor.
   */
  function handleCartaoFixo(tipo: 'medica' | 'inss', mudanca: Partial<CartaoFixo>) {
    onMudar({ ...campos, [tipo]: { ...campos[tipo], ...mudanca } });
  }

  return (
    <div>
      <LinhaDeCampos colunas={2}>
        <Campo rotulo="Funcionário" id="ac-favorecido">
          <input
            id="ac-favorecido"
            type="text"
            list="ac-favorecidos"
            placeholder="Nome do funcionário"
            autoComplete="off"
            value={campos.funcionario}
            onChange={(evento) =>
              onMudar(
                aoEscolherFuncionario(
                  { ...campos, funcionario: evento.target.value },
                  favorecidos,
                  nomes,
                ),
              )
            }
            className={CLASSE_DO_CONTROLE}
          />
          <datalist id="ac-favorecidos">
            {funcionariosDaCalculadora(favorecidos).map((favorecido) => (
              <option key={favorecido.id} value={favorecido.nome} />
            ))}
          </datalist>
        </Campo>
        <Campo rotulo="CPF do funcionário" id="ac-cpf">
          <input
            id="ac-cpf"
            type="text"
            placeholder="000.000.000-00"
            value={campos.cpf}
            onChange={(evento) => onMudar({ ...campos, cpf: evento.target.value })}
            className={CLASSE_DO_CONTROLE}
          />
        </Campo>
      </LinhaDeCampos>
      <LinhaDeCampos colunas={3}>
        <Campo rotulo="Pagador (a débito de)" id="ac-pagador">
          <input
            id="ac-pagador"
            type="text"
            value={campos.pagador}
            onChange={(evento) => onMudar({ ...campos, pagador: evento.target.value })}
            className={CLASSE_DO_CONTROLE}
          />
        </Campo>
        <Campo rotulo="Mês de competência" id="ac-mes">
          <select
            id="ac-mes"
            value={campos.mes}
            onChange={(evento) => onMudar(comMesEAno(campos, { mes: evento.target.value }))}
            className={CLASSE_DO_SELECT}
          >
            {MESES_POR_EXTENSO.map((nome, indice) => (
              <option key={nome} value={String(indice + 1)}>
                {nome}
              </option>
            ))}
          </select>
        </Campo>
        <Campo rotulo="Ano" id="ac-ano">
          <input
            id="ac-ano"
            type="number"
            value={campos.ano}
            onChange={(evento) => onMudar(comMesEAno(campos, { ano: evento.target.value }))}
            className={CLASSE_DO_CONTROLE}
          />
        </Campo>
      </LinhaDeCampos>
      <LinhaDeCampos colunas={2}>
        <Campo rotulo="Data do lançamento" id="ac-data">
          <input
            id="ac-data"
            type="date"
            value={campos.data}
            onChange={(evento) => onMudar({ ...campos, data: evento.target.value })}
            className={CLASSE_DO_CONTROLE}
          />
          <div className="mt-[3px] text-[10.5px] text-texto-fraco">
            Dia 30 (ou último dia útil anterior), igual à regra dos salários.
          </div>
        </Campo>
        <div className="mb-[13px] flex items-end">
          <Botao variante="fantasma" tamanho="sm" className="w-full" onClick={onRepetir}>
            <IconRepeat size={13} aria-hidden="true" /> Repetir último lançamento
          </Botao>
        </div>
      </LinhaDeCampos>

      <label className={cn(CLASSE_DA_MARCA, 'my-3')}>
        <input
          type="checkbox"
          checked={campos.ferias}
          onChange={(evento) => onMudar({ ...campos, ferias: evento.target.checked })}
        />
        <span className="text-[13px] font-semibold text-foreground">
          Incluir 1/3 de férias sobre os valores abaixo (× 4/3)
        </span>
      </label>

      {(['custo', 'diarias'] as const).map((tipo) => (
        <CartaoDeTipo key={tipo} hidden={!visivel(tipo)}>
          <label className={CLASSE_DA_MARCA}>
            <input
              type="checkbox"
              checked={campos[tipo].incluido}
              onChange={(evento) =>
                handleCartaoComDiaria(tipo, { incluido: evento.target.checked })
              }
            />
            <span className="text-[13px] font-semibold">
              {tipo === 'custo' ? 'Ajuda de Custo' : 'Diárias'}
            </span>
          </label>
          <div className="mt-2 grid grid-cols-3 gap-2">
            <Campo rotulo="Valor diária (R$)" id={`ac-${tipo}-diaria`}>
              <input
                id={`ac-${tipo}-diaria`}
                type="text"
                inputMode="decimal"
                placeholder="0,00"
                value={campos[tipo].diaria}
                onChange={(evento) =>
                  handleCartaoComDiaria(tipo, {
                    diaria: mascararDecimalLivre(evento.target.value),
                  })
                }
                className={cn(CLASSE_DO_CONTROLE, 'text-right')}
              />
            </Campo>
            <Campo rotulo="Dias" id={`ac-${tipo}-dias`}>
              <input
                id={`ac-${tipo}-dias`}
                type="number"
                min={0}
                step={1}
                value={campos[tipo].dias}
                onChange={(evento) =>
                  handleCartaoComDiaria(tipo, { dias: evento.target.value })
                }
                className={CLASSE_DO_CONTROLE}
              />
            </Campo>
            <Campo rotulo="Subtotal" id={`ac-${tipo}-subtotal`}>
              <div id={`ac-${tipo}-subtotal`} className={CLASSE_DO_SUBTOTAL}>
                {formatarReais(subtotais[tipo].final)}
              </div>
            </Campo>
          </div>
        </CartaoDeTipo>
      ))}

      {(['medica', 'inss'] as const).map((tipo) => (
        <CartaoDeTipo key={tipo} hidden={!visivel(tipo)}>
          <label className={CLASSE_DA_MARCA}>
            <input
              type="checkbox"
              checked={campos[tipo].incluido}
              onChange={(evento) =>
                handleCartaoFixo(tipo, { incluido: evento.target.checked })
              }
            />
            <span className="text-[13px] font-semibold">
              {tipo === 'medica' ? 'Auxílio plano de saúde' : 'Devolução de INSS'}
            </span>
          </label>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <Campo rotulo="Valor (R$)" id={`ac-${tipo}-valor`}>
              <input
                id={`ac-${tipo}-valor`}
                type="text"
                inputMode="decimal"
                placeholder="0,00"
                value={campos[tipo].valor}
                onChange={(evento) =>
                  handleCartaoFixo(tipo, { valor: mascararCentavos(evento.target.value) })
                }
                className={cn(CLASSE_DO_CONTROLE, 'text-right')}
              />
            </Campo>
            <Campo rotulo="Subtotal" id={`ac-${tipo}-subtotal`}>
              <div id={`ac-${tipo}-subtotal`} className={CLASSE_DO_SUBTOTAL}>
                {formatarReais(subtotais[tipo])}
              </div>
            </Campo>
          </div>
          <div className="mt-1 text-[10.5px] text-texto-fraco">{SEM_FERIAS}</div>
        </CartaoDeTipo>
      ))}

      {campos.ferias && algumIncluido && (
        <div className="mt-2 rounded-[8px] border border-emb-ferias-borda bg-emb-ferias-fundo px-3 py-2.5">
          <div className="mb-1.5 text-[12.5px] font-semibold text-brass-light">
            Discriminação do cálculo com 1/3 de férias
          </div>
          <div className="flex flex-col gap-1 text-[12px] text-foreground">
            {(['custo', 'diarias'] as const)
              .filter((tipo) => campos[tipo].incluido)
              .map((tipo) => (
                <div key={tipo}>
                  <strong>{tipo === 'custo' ? 'Ajuda de Custo' : 'Diárias'}:</strong> base{' '}
                  {formatarReais(subtotais[tipo].base)} + 1/3 de férias (
                  {formatarReais(subtotais[tipo].final - subtotais[tipo].base)}) ={' '}
                  <strong>{formatarReais(subtotais[tipo].final)}</strong>
                </div>
              ))}
            {(['medica', 'inss'] as const)
              .filter((tipo) => campos[tipo].incluido)
              .map((tipo) => (
                <div key={tipo}>
                  <strong>
                    {tipo === 'medica' ? 'Auxílio plano de saúde' : 'Devolução de INSS'}:
                  </strong>{' '}
                  {formatarReais(subtotais[tipo])}{' '}
                  <span className="text-texto-fraco">(não recebe 1/3 de férias)</span>
                </div>
              ))}
          </div>
        </div>
      )}

      <div className="mt-1.5 flex justify-end text-[15px] text-foreground">
        <strong>Total geral: {formatarReais(subtotais.total)}</strong>
      </div>
      <div className="mt-2.5 text-[11px] text-texto-fraco">
        Ao confirmar, cria um lançamento Pendente para cada tipo marcado acima (discriminados,
        como na guia DAE). O recibo é gerado depois, quando você marcar o grupo inteiro como
        pago.
      </div>
    </div>
  );
}
